import React, { useEffect, useState, useRef } from 'react';
import { Plus, Download, Search, Upload, FileSpreadsheet, LogIn, LogOut, ScrollText, Check, AlertCircle, X, Pencil, Trash2 } from 'lucide-react';
import { Contributor, Solution, AvailabilityMap, AvailabilityStatus, Region, REGIONS, ExperienceLevel, ExperienceLevelMap, EXPERIENCE_LABELS, OOORange } from '../types';
import { AvailabilityGrid } from './AvailabilityGrid';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Modal } from './ui/Modal';
import { useAdobeImsAuth } from '../auth/useAdobeImsAuth';
import { logAvailabilityChange, logExperienceLevelChange, upsertContributorRecord, deleteContributorRecord, reorderContributorRecords, upsertSolutionRecord, deleteSolutionRecord, reorderSolutionRecords, fetchInitialStateFromDatabase, fetchCurrentWeekSchedule, fetchNextWeekSchedule, updateDailyAvailability, fetchAuditLog, fetchOOORanges, createOOORange, updateOOORange, deleteOOORange } from '../utils/database';
import { ContributorWeekView } from './ContributorWeekView';
import { normalizeHydratedSolutions, reindexSolutions } from '../utils/solutionOrder';
import { normalizeHydratedContributors, reindexContributors } from '../utils/contributorOrder';
import { isLongLeaveRange, calendarDaysInclusive } from '../utils/longLeave';
function stableSlugId(input: string) {
  const normalized = input.trim().toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
  const slug = normalized.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  if (slug) {
    return slug;
  }
  let hash = 0;
  for (const char of normalized || input.trim().toLowerCase()) {
    hash = hash * 31 + char.codePointAt(0)! >>> 0;
  }
  return `id-${hash.toString(16)}`;
}
const STATUS_LABELS: Record<AvailabilityStatus, string> = {
  available: 'Qualified and Available',
  limited: 'Qualified not Available',
  unavailable: 'Not Qualified'
};
const STATUS_COLORS: Record<AvailabilityStatus, {
  bg: string;
  text: string;
  border: string;
}> = {
  available: {
    bg: 'bg-green-100',
    text: 'text-green-700',
    border: 'border-green-300'
  },
  limited: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-700',
    border: 'border-yellow-300'
  },
  unavailable: {
    bg: 'bg-red-100',
    text: 'text-red-700',
    border: 'border-red-300'
  }
};
export function Dashboard() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    accessLevel,
    canManageData,
    error: authError,
    isLoading: isAuthLoading,
    isReady: isAuthReady,
    isSignedIn,
    signIn,
    signOut,
    user
  } = useAdobeImsAuth();
  const isAdmin = accessLevel === 'superadmin' || accessLevel === 'admin';
  const hasDashboardDataAccess = isSignedIn && accessLevel !== 'unregistered' && accessLevel !== 'anonymous';
  // Filter state
  const [selectedContributorId, setSelectedContributorId] = useState<string | null>(null);
  const [selectedSolutionId, setSelectedSolutionId] = useState<string | null>(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<AvailabilityStatus | null>(null);
  const [selectedExperienceFilter, setSelectedExperienceFilter] = useState<ExperienceLevel | null>(null);
  const [selectedRegionFilter, setSelectedRegionFilter] = useState<Region | null>(null);
  const [contributors, setContributors] = useState<Contributor[]>([]);
  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [availability, setAvailability] = useState<AvailabilityMap>({});
  const [experienceLevels, setExperienceLevels] = useState<ExperienceLevelMap>({});
  const [weekViewContributorId, setWeekViewContributorId] = useState<string | null>(null); // full-grid weekly view (name click)
  const [weekPanelContributorId, setWeekPanelContributorId] = useState<string | null>(null); // slide-over panel (icon click)
  const [isHydrating, setIsHydrating] = useState(true);
  const [dbFeedback, setDbFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const dbFeedbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const notifyDbResult = (result: {
    success: boolean;
    error?: string;
  }) => {
    if (dbFeedbackTimer.current) clearTimeout(dbFeedbackTimer.current);
    if (result.success) {
      setDbFeedback({
        type: 'success',
        message: 'Saved'
      });
      dbFeedbackTimer.current = setTimeout(() => setDbFeedback(null), 2000);
    } else {
      setDbFeedback({
        type: 'error',
        message: 'Save failed — please try again'
      });
      dbFeedbackTimer.current = setTimeout(() => setDbFeedback(null), 5000);
    }
  };
  useEffect(() => {
    let isCancelled = false;
    if (!isAuthReady) {
      setIsHydrating(true);
      return () => {
        isCancelled = true;
      };
    }
    if (!isSignedIn) {
      if (!isCancelled) {
        setContributors([]);
        setSolutions([]);
        setAvailability({});
        setExperienceLevels({});
        setIsHydrating(false);
      }
      return () => {
        isCancelled = true;
      };
    }
    if (isSignedIn && !hasDashboardDataAccess) {
      if (!isCancelled) {
        setContributors([]);
        setSolutions([]);
        setAvailability({});
        setExperienceLevels({});
        setIsHydrating(false);
      }
      return () => {
        isCancelled = true;
      };
    }
    const loadInitialState = async () => {
      setIsHydrating(true);
      const databaseState = await fetchInitialStateFromDatabase();
      if (isCancelled) {
        return;
      }

      // Security: never fall back to sessionStorage or buildInitialState() when db-get-state
      // fails or returns 403. Client-spoofed accessLevel could otherwise load demo/cached data
      // without a successful server-authorized read.
      if (!databaseState) {
        setContributors([]);
        setSolutions([]);
        setAvailability({});
        setExperienceLevels({});
        setIsHydrating(false);
        return;
      }

      // Normalize OOO flags against today's date so stale isOutOfOffice values are corrected.
      const todayStr = new Date().toISOString().split('T')[0];
      const rawContributors = normalizeHydratedContributors(databaseState.contributors);
      const normalizedContributors = rawContributors.map(c => {
        if (c.oooStartDate && c.oooEndDate) {
          const shouldBeOOO = c.oooStartDate <= todayStr && c.oooEndDate >= todayStr;
          if (c.isOutOfOffice !== shouldBeOOO) return {
            ...c,
            isOutOfOffice: shouldBeOOO
          };
        }
        return c;
      });
      setContributors(normalizedContributors);
      setSolutions(normalizeHydratedSolutions(databaseState.solutions));
      setAvailability(databaseState.availability);
      setExperienceLevels(databaseState.experienceLevels);
      setIsHydrating(false);
    };
    loadInitialState();
    return () => {
      isCancelled = true;
    };
  }, [isAuthReady, isSignedIn, accessLevel]);

  // Modal States
  const [isAddICOpen, setIsAddICOpen] = useState(false);
  const [isAddSolutionOpen, setIsAddSolutionOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isEditContributorOpen, setIsEditContributorOpen] = useState(false);
  const [isEditSolutionOpen, setIsEditSolutionOpen] = useState(false);
  const [newICName, setNewICName] = useState('');
  const [newICEmail, setNewICEmail] = useState('');
  const [newICRegion, setNewICRegion] = useState<Region>('USA');
  const [newICNameError, setNewICNameError] = useState('');
  const [newICEmailError, setNewICEmailError] = useState('');
  const [newSolutionName, setNewSolutionName] = useState('');
  const [newSolutionNameError, setNewSolutionNameError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [importError, setImportError] = useState<string>('');
  const [editingContributor, setEditingContributor] = useState<Contributor | null>(null);
  const [editingSolution, setEditingSolution] = useState<Solution | null>(null);
  const [editContributorName, setEditContributorName] = useState('');
  const [editContributorRegion, setEditContributorRegion] = useState<Region>('USA');
  const [editContributorDisplay, setEditContributorDisplay] = useState(true);
  const [editSolutionName, setEditSolutionName] = useState('');
  // OOO modal state — manages the full history of OOO ranges for one contributor
  const [isOOOModalOpen, setIsOOOModalOpen] = useState(false);
  const [oooTargetContributorId, setOOOTargetContributorId] = useState<string | null>(null);
  const [oooRanges, setOOORanges] = useState<OOORange[]>([]);
  const [oooRangesLoading, setOOORangesLoading] = useState(false);
  const [oooNewStartDate, setOOONewStartDate] = useState('');
  const [oooNewEndDate, setOOONewEndDate] = useState('');
  const [oooAddingRange, setOOOAddingRange] = useState(false);
  const [oooEditingRangeId, setOOOEditingRangeId] = useState<string | null>(null);
  const [oooEditStartDate, setOOOEditStartDate] = useState('');
  const [oooEditEndDate, setOOOEditEndDate] = useState('');
  const [oooError, setOOOError] = useState('');
  const requestManagementAccess = () => {
    if (!canManageData && !isSignedIn && !isAuthLoading) {
      void signIn();
    }
    return canManageData;
  };
  const requestAdminAccess = () => {
    if (!isAdmin && !isSignedIn && !isAuthLoading) {
      void signIn();
    }
    return isAdmin;
  };
  const handleAuthButtonClick = () => {
    if (isSignedIn) {
      void signOut();
      return;
    }
    void signIn();
  };
  useEffect(() => {
    if (canManageData) {
      if (isAdmin) {
        return;
      }
      setIsAddICOpen(false);
      setIsAddSolutionOpen(false);
      setIsImportOpen(false);
      return;
    }
    setIsAddICOpen(false);
    setIsAddSolutionOpen(false);
    setIsImportOpen(false);
    setIsEditContributorOpen(false);
    setIsEditSolutionOpen(false);
    setIsOOOModalOpen(false);
  }, [canManageData, isAdmin]);
  // Download current data as CSV (for editing and re-importing)
  const handleDownloadCurrentData = () => {
    if (!requestAdminAccess()) return;
    const headers = ['Contributor', 'Region', ...solutions.map(s => s.name)];
    const rows = contributors.map(contributor => {
      const cells = solutions.map(solution => {
        const status = availability[contributor.id]?.[solution.id] || 'unavailable';
        const level = experienceLevels[contributor.id]?.[solution.id];
        return level ? `${status}:${level}` : status;
      });
      return [contributor.name, contributor.region, ...cells].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const date = new Date().toISOString().split('T')[0];
    link.download = `availability-data-${date}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  const [isExportingLog, setIsExportingLog] = useState(false);
  const handleDownloadAuditLog = async () => {
    if (!requestAdminAccess()) return;
    setIsExportingLog(true);
    try {
      const entries = await fetchAuditLog();
      if (!entries || entries.length === 0) {
        alert('No audit log entries found.');
        return;
      }

      // Collect all unique keys across all entries for CSV columns
      const keySet = new Set<string>();
      for (const entry of entries) {
        for (const key of Object.keys(entry)) keySet.add(key);
      }
      // Preferred column order; any remaining keys are appended
      const orderedKeys = ['_id', 'timestamp', 'actionType', 'actorId', 'contributorId', 'contributorName', 'region', 'solutionId', 'solutionName', 'status', 'startDate', 'endDate', 'notes'];
      const extraKeys = [...keySet].filter(k => !orderedKeys.includes(k)).sort();
      const columns = [...orderedKeys.filter(k => keySet.has(k)), ...extraKeys];
      const escape = (v: any) => {
        if (v === null || v === undefined) return '';
        const s = String(v);
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const csvRows = [columns.join(','), ...entries.map(e => columns.map(k => escape(e[k])).join(','))];
      const blob = new Blob([csvRows.join('\n')], {
        type: 'text/csv'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsExportingLog(false);
    }
  };

  // Download template CSV
  const handleDownloadTemplate = () => {
    if (!requestAdminAccess()) return;
    const headers = ['Contributor', 'Region', ...solutions.map(s => s.name)];
    const exampleRow = ['John Doe', 'USA', ...solutions.map((_, i) => {
      // Show varied examples
      if (i === 0) return 'available:L3';
      if (i === 1) return 'limited:L2';
      if (i === 2) return 'unavailable';
      return 'available:L1';
    })];
    const csvContent = [headers.join(','), exampleRow.join(','), ['Jane Smith', 'EMEA', ...solutions.map(() => '')].join(',')].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'availability-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };
  // Parse and import CSV
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!requestAdminAccess()) return;
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length < 2) {
          setImportError('File must contain at least a header row and one data row');
          return;
        }
        // Parse header
        const headers = lines[0].split(',').map(h => h.trim());
        const contributorIndex = headers.findIndex(h => h.toLowerCase() === 'contributor');
        const regionIndex = headers.findIndex(h => h.toLowerCase() === 'region');
        if (contributorIndex === -1 || regionIndex === -1) {
          setImportError('CSV must have "Contributor" and "Region" columns');
          return;
        }
        const solutionHeaders = headers.slice(2); // Everything after Contributor and Region
        // Create solutions from headers
        const newSolutions: Solution[] = reindexSolutions(solutionHeaders.map(name => ({
          // id: crypto.randomUUID(),
          id: stableSlugId(name.trim()),
          name: name.trim()
        })));
        // Parse data rows
        const newContributors: Contributor[] = [];
        const newAvailability: AvailabilityMap = {};
        const newExperienceLevels: ExperienceLevelMap = {};
        const validLevels = ['L1', 'L2', 'L3', 'L4'];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim());
          const name = values[contributorIndex];
          const region = values[regionIndex] as Region;
          if (!name || !REGIONS.includes(region)) continue;
          // const contributorId = crypto.randomUUID();
          const contributorId = stableSlugId(name);
          newContributors.push({
            id: contributorId,
            name,
            email: '',
            region
          });
          // Parse availability and experience level for each solution
          // Format: "status" or "status:level" (e.g., "available:L2")
          newAvailability[contributorId] = {};
          newExperienceLevels[contributorId] = {};
          solutionHeaders.forEach((_, idx) => {
            const cellValue = values[idx + 2]?.trim() || '';
            const solutionId = newSolutions[idx].id;
            const parts = cellValue.split(':').map(p => p.trim());
            const statusPart = parts[0]?.toLowerCase();
            const levelPart = parts[1]?.toUpperCase();
            // Parse status
            if (statusPart === 'available' || statusPart === 'limited' || statusPart === 'unavailable') {
              newAvailability[contributorId][solutionId] = statusPart as AvailabilityStatus;
            } else {
              newAvailability[contributorId][solutionId] = 'unavailable';
            }
            // Parse experience level
            if (levelPart && validLevels.includes(levelPart)) {
              newExperienceLevels[contributorId][solutionId] = levelPart as ExperienceLevel;
            }
          });
        }
        // Update state
        setContributors(newContributors);
        setSolutions(newSolutions);
        setAvailability(newAvailability);
        setExperienceLevels(newExperienceLevels);
        setImportError('');
        setIsImportOpen(false);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        setImportError('Failed to parse CSV file. Please check the format.');
        console.error('CSV parse error:', error);
      }
    };
    reader.readAsText(file);
  };
  // Helper to update lastUpdated timestamp for a contributor
  const touchContributor = (contributorId: string) => {
    setContributors(prev => prev.map(c => c.id === contributorId ? {
      ...c,
      lastUpdated: new Date().toISOString()
    } : c));
  };
  const saveContributorRecord = (contributor: Contributor) => {
    upsertContributorRecord(contributor).then(notifyDbResult);
  };
  const saveSolutionOrder = (orderedSolutions: Solution[]) => {
    if (orderedSolutions.length === 0) return;
    reorderSolutionRecords(orderedSolutions.map(solution => ({
      id: solution.id,
      displayOrder: solution.displayOrder
    }))).then(notifyDbResult);
  };
  const saveContributorOrder = (orderedContributors: Contributor[]) => {
    if (orderedContributors.length === 0) return;
    reorderContributorRecords(orderedContributors.map(contributor => ({
      id: contributor.id,
      displayOrder: contributor.displayOrder
    }))).then(notifyDbResult);
  };
  // Handlers
  const handleToggleAvailability = async (contributorId: string, solutionId: string) => {
    if (!requestManagementAccess()) return;
    const contributor = contributors.find(c => c.id === contributorId);
    const solution = solutions.find(s => s.id === solutionId);

    // The grid displays and edits base availability only (weekly overrides are
    // managed in the week view and never surface here). Cycle from base.
    const currentStatus = availability[contributorId]?.[solutionId] ?? 'unavailable';
    const statusCycle: Record<AvailabilityStatus, AvailabilityStatus> = {
      unavailable: 'limited',
      limited: 'available',
      available: 'unavailable'
    };
    const newStatus = statusCycle[currentStatus];
    setAvailability(prev => ({
      ...prev,
      [contributorId]: {
        ...(prev[contributorId] ?? {}),
        [solutionId]: newStatus
      }
    }));
    if (contributor && solution) {
      logAvailabilityChange(contributor, solution, newStatus, experienceLevels[contributorId]?.[solutionId]).then(notifyDbResult);
    }
    touchContributor(contributorId);
  };
  const handleCycleExperienceLevel = async (contributorId: string, solutionId: string) => {
    if (!requestManagementAccess()) return;
    const contributor = contributors.find(c => c.id === contributorId);
    const solution = solutions.find(s => s.id === solutionId);
    const contributorLevels = experienceLevels[contributorId] || {};
    const currentLevel = contributorLevels[solutionId];
    const cycle: Record<string, ExperienceLevel | undefined> = {
      undefined: 'L1',
      L1: 'L2',
      L2: 'L3',
      L3: 'L4',
      L4: undefined as any
    };
    const newLevel = cycle[String(currentLevel)];
    setExperienceLevels(prev => {
      const nextContributorLevels = {
        ...(prev[contributorId] || {})
      };
      if (newLevel) {
        nextContributorLevels[solutionId] = newLevel;
      } else {
        delete nextContributorLevels[solutionId];
      }
      return {
        ...prev,
        [contributorId]: nextContributorLevels
      };
    });
    if (contributor && solution) {
      const currentStatus = availability[contributorId]?.[solutionId] || 'unavailable';
      logExperienceLevelChange(contributor, solution, newLevel, currentStatus).then(notifyDbResult);
    }
    touchContributor(contributorId);
  };
  const handleUpdateContributorRegion = (id: string, region: Region) => {
    if (!requestManagementAccess()) return;
    const contributor = contributors.find(c => c.id === id);
    const updatedContributor = contributor ? {
      ...contributor,
      region,
      lastUpdated: new Date().toISOString()
    } : null;
    setContributors(prev => prev.map(c => c.id === id ? {
      ...c,
      region,
      lastUpdated: updatedContributor?.lastUpdated || c.lastUpdated
    } : c));
    if (updatedContributor) {
      saveContributorRecord(updatedContributor);
    }
  };
  const handleAddContributor = async () => {
    if (!requestAdminAccess()) return;
    let hasError = false;
    if (!newICName.trim()) {
      setNewICNameError('Name is required.');
      hasError = true;
    } else {
      setNewICNameError('');
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!newICEmail.trim()) {
      setNewICEmailError('Email is required.');
      hasError = true;
    } else if (!emailRegex.test(newICEmail.trim())) {
      setNewICEmailError('Enter a valid email address.');
      hasError = true;
    } else {
      setNewICEmailError('');
    }
    if (hasError) return;
    const contributorName = newICName.trim();
    const contributorId = stableSlugId(contributorName);
    if (!contributorId) {
      setNewICNameError('Unable to generate a valid ID for that name.');
      return;
    }
    if (contributors.some(contributor => contributor.id === contributorId)) {
      setNewICNameError('A contributor with that name already exists.');
      return;
    }
    const newContributor: Contributor = {
      // id: crypto.randomUUID(),
      id: contributorId,
      name: contributorName,
      email: newICEmail.trim(),
      region: newICRegion,
      lastUpdated: new Date().toISOString()
    };
    const nextContributors = reindexContributors([...contributors, newContributor]);
    const createdContributor = nextContributors.find(c => c.id === newContributor.id) || nextContributors[nextContributors.length - 1];
    const response = await upsertContributorRecord(createdContributor);
    notifyDbResult(response);
    if (!response.success) return;
    setContributors(nextContributors);
    setNewICName('');
    setNewICEmail('');
    setNewICRegion('USA');
    setNewICNameError('');
    setNewICEmailError('');
    setIsAddICOpen(false);
  };
  const handleAddSolution = () => {
    if (!requestAdminAccess()) return;
    if (!newSolutionName.trim()) {
      setNewSolutionNameError('Solution name is required.');
      return;
    }
    const solutionName = newSolutionName.trim();
    if (solutions.some(s => s.name.toLowerCase() === solutionName.toLowerCase())) {
      setNewSolutionNameError('A solution with that name already exists.');
      return;
    }
    setNewSolutionNameError('');
    const newSolution: Solution = {
      // id: crypto.randomUUID(),
      id: stableSlugId(solutionName),
      name: solutionName
    };
    const nextSolutions = reindexSolutions([...solutions, newSolution]);
    const createdSolution = nextSolutions.find(solution => solution.id === newSolution.id) || nextSolutions[nextSolutions.length - 1];
    setSolutions(nextSolutions);
    if (createdSolution) {
      upsertSolutionRecord(createdSolution).then(notifyDbResult);
    }
    setNewSolutionName('');
    setNewSolutionNameError('');
    setIsAddSolutionOpen(false);
  };
  const handleDeleteContributor = async (id: string) => {
    if (!requestAdminAccess()) return;
    if (confirm('Are you sure you want to remove this contributor?')) {
      const contributor = contributors.find(c => c.id === id);
      const nextContributors = reindexContributors(contributors.filter(c => c.id !== id));
      setContributors(nextContributors);
      setAvailability(prev => {
        const next = {
          ...prev
        };
        delete next[id];
        return next;
      });
      setExperienceLevels(prev => {
        const next = {
          ...prev
        };
        delete next[id];
        return next;
      });
      if (contributor) {
        const result = await deleteContributorRecord({
          id: contributor.id,
          name: contributor.name,
          region: contributor.region
        });
        notifyDbResult(result);
      }
      saveContributorOrder(nextContributors);
    }
  };
  const handleDeleteSolution = async (id: string) => {
    if (!requestAdminAccess()) return;
    if (confirm('Are you sure you want to remove this solution?')) {
      const solution = solutions.find(s => s.id === id);
      const nextSolutions = reindexSolutions(solutions.filter(s => s.id !== id));
      setSolutions(nextSolutions);
      setAvailability(prev => Object.fromEntries(Object.entries(prev).map(([contributorId, solutionMap]) => {
        const nextSolutionMap = {
          ...solutionMap
        };
        delete nextSolutionMap[id];
        return [contributorId, nextSolutionMap];
      })));
      setExperienceLevels(prev => Object.fromEntries(Object.entries(prev).map(([contributorId, solutionMap]) => {
        const nextSolutionMap = {
          ...solutionMap
        };
        delete nextSolutionMap[id];
        return [contributorId, nextSolutionMap];
      })));
      if (solution) {
        const result = await deleteSolutionRecord({
          id: solution.id,
          name: solution.name
        });
        notifyDbResult(result);
      }
      saveSolutionOrder(nextSolutions);
    }
  };
  // Status filter handler
  const handleStatusFilterClick = (status: AvailabilityStatus) => {
    setSelectedStatusFilter(selectedStatusFilter === status ? null : status);
  };
  // Experience level filter handler
  const handleExperienceFilterClick = (level: ExperienceLevel) => {
    setSelectedExperienceFilter(selectedExperienceFilter === level ? null : level);
  };
  // Region filter handler
  const handleRegionFilterClick = (region: Region) => {
    setSelectedRegionFilter(selectedRegionFilter === region ? null : region);
  };
  // Filter handlers
  const handleContributorClick = (contributorId: string) => {
    setWeekViewContributorId(weekViewContributorId === contributorId ? null : contributorId);
  };
  const handleCloseWeekView = () => {
    setWeekViewContributorId(null);
  };
  const handleSolutionClick = (solutionId: string) => {
    setSelectedSolutionId(selectedSolutionId === solutionId ? null : solutionId);
  };
  const handleClearFilters = () => {
    setSelectedContributorId(null);
    setSelectedSolutionId(null);
    setSelectedStatusFilter(null);
    setSelectedExperienceFilter(null);
    setSelectedRegionFilter(null);
  };
  // Filtered views - apply search AND click filters
  const filteredContributors = contributors.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredSolutions = solutions.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const hasContributorMatch = filteredContributors.length > 0;
  const hasSolutionMatch = filteredSolutions.length > 0;
  let displayContributors = searchQuery === '' ? contributors : hasContributorMatch ? filteredContributors : contributors;
  let displaySolutions = searchQuery === '' ? solutions : hasSolutionMatch ? filteredSolutions : solutions;
  // Hide contributors flagged as not displayed for viewer/editor access levels;
  // admins and superadmins always see them
  if (!isAdmin) {
    displayContributors = displayContributors.filter(c => c.display !== false);
  }
  // Apply click filters
  if (selectedContributorId) {
    displayContributors = displayContributors.filter(c => c.id === selectedContributorId);
  }
  if (selectedSolutionId) {
    displaySolutions = displaySolutions.filter(s => s.id === selectedSolutionId);
  }
  // Apply region filter
  if (selectedRegionFilter) {
    displayContributors = displayContributors.filter(c => c.region === selectedRegionFilter);
  }
  // Apply status + experience filters - supports multi-select (both can be active)
  let statusFilteredContributors = displayContributors;
  let statusFilteredSolutions = displaySolutions;
  if (selectedStatusFilter || selectedExperienceFilter) {
    const contributorIdsMatching = new Set<string>();
    const solutionIdsMatching = new Set<string>();
    displayContributors.forEach(contributor => {
      displaySolutions.forEach(solution => {
        const status = availability[contributor.id]?.[solution.id] || 'unavailable';
        const expLevel = experienceLevels[contributor.id]?.[solution.id];
        const statusMatch = !selectedStatusFilter || status === selectedStatusFilter;
        const expMatch = !selectedExperienceFilter || expLevel === selectedExperienceFilter;
        if (statusMatch && expMatch) {
          contributorIdsMatching.add(contributor.id);
          solutionIdsMatching.add(solution.id);
        }
      });
    });
    statusFilteredContributors = displayContributors.filter(c => contributorIdsMatching.has(c.id));
    statusFilteredSolutions = displaySolutions.filter(s => solutionIdsMatching.has(s.id));
  }
  const selectedContributor = contributors.find(c => c.id === selectedContributorId);
  const selectedSolution = solutions.find(s => s.id === selectedSolutionId);
  const hasActiveFilters = selectedContributorId !== null || selectedSolutionId !== null || selectedStatusFilter !== null || selectedExperienceFilter !== null || selectedRegionFilter !== null;
  // Reorder handlers
  const handleReorderContributors = (fromIndex: number, toIndex: number) => {
    if (!requestAdminAccess()) return;
    const newContributors = [...contributors];
    const [removed] = newContributors.splice(fromIndex, 1);
    newContributors.splice(toIndex, 0, removed);
    const reorderedContributors = reindexContributors(newContributors);
    setContributors(reorderedContributors);
    saveContributorOrder(reorderedContributors);
  };
  const handleReorderSolutions = (fromIndex: number, toIndex: number) => {
    if (!requestAdminAccess()) return;
    const newSolutions = [...solutions];
    const [removed] = newSolutions.splice(fromIndex, 1);
    newSolutions.splice(toIndex, 0, removed);
    const reorderedSolutions = reindexSolutions(newSolutions);
    setSolutions(reorderedSolutions);
    saveSolutionOrder(reorderedSolutions);
  };
  // Edit handlers
  const handleEditContributor = (contributor: Contributor) => {
    if (!requestAdminAccess()) return;
    setEditingContributor(contributor);
    setEditContributorName(contributor.name);
    setEditContributorRegion(contributor.region);
    setEditContributorDisplay(contributor.display ?? true);
    setIsEditContributorOpen(true);
  };
  const handleSaveContributor = () => {
    if (!requestAdminAccess()) return;
    if (!editingContributor || !editContributorName.trim()) return;
    const updatedContributor: Contributor = {
      ...editingContributor,
      name: editContributorName.trim(),
      region: editContributorRegion,
      display: editContributorDisplay,
      lastUpdated: new Date().toISOString()
    };
    setContributors(prev => prev.map(c => c.id === editingContributor.id ? updatedContributor : c));
    saveContributorRecord(updatedContributor);
    setIsEditContributorOpen(false);
    setEditingContributor(null);
    setEditContributorName('');
  };
  const handleEditSolution = (solution: Solution) => {
    if (!requestAdminAccess()) return;
    setEditingSolution(solution);
    setEditSolutionName(solution.name);
    setIsEditSolutionOpen(true);
  };
  const handleSaveSolution = () => {
    if (!requestAdminAccess()) return;
    if (!editingSolution || !editSolutionName.trim()) return;
    const nextSolutions = reindexSolutions(solutions.map(s => s.id === editingSolution.id ? {
      ...s,
      name: editSolutionName.trim()
    } : s));
    const updatedSolution = nextSolutions.find(s => s.id === editingSolution.id);
    setSolutions(nextSolutions);
    if (updatedSolution) {
      upsertSolutionRecord(updatedSolution).then(notifyDbResult);
    }
    setIsEditSolutionOpen(false);
    setEditingSolution(null);
    setEditSolutionName('');
  };
  // Applies a contributor's derived OOO cache (returned by db-update-ooo-range)
  // back onto local contributor state, so grid/week-view badges stay in sync.
  const applyOOOCacheToContributor = (contributorId: string, cache: {
    oooStartDate: string | null;
    oooEndDate: string | null;
    isOutOfOffice: boolean;
  }) => {
    setContributors(prev => prev.map(c => c.id === contributorId ? {
      ...c,
      isOutOfOffice: cache.isOutOfOffice,
      oooStartDate: cache.oooStartDate || undefined,
      oooEndDate: cache.oooEndDate || undefined,
      lastUpdated: new Date().toISOString()
    } : c));
  };

  // OOO handler - opens the multi-range history modal for a contributor
  const handleOpenOOOModal = (contributorId: string) => {
    if (!requestManagementAccess()) return;
    setOOOTargetContributorId(contributorId);
    setOOORanges([]);
    setOOONewStartDate('');
    setOOONewEndDate('');
    setOOOEditingRangeId(null);
    setOOOError('');
    setOOOAddingRange(false);
    setIsOOOModalOpen(true);
    setOOORangesLoading(true);
    fetchOOORanges(contributorId).then(ranges => {
      setOOORanges(ranges || []);
      setOOORangesLoading(false);
    });
  };
  const handleAddOOORange = () => {
    if (!requestManagementAccess()) return;
    if (!oooTargetContributorId) return;
    setOOOError('');
    if (!oooNewStartDate || !oooNewEndDate) {
      setOOOError('Please select both start and end dates');
      return;
    }
    if (oooNewEndDate < oooNewStartDate) {
      setOOOError('End date must be on or after start date');
      return;
    }
    setOOOAddingRange(true);
    createOOORange(oooTargetContributorId, oooNewStartDate, oooNewEndDate).then(result => {
      setOOOAddingRange(false);
      if (!result.success || !result.range) {
        setOOOError(result.error || 'Failed to add OOO range');
        return;
      }
      setOOORanges(prev => [...prev, result.range as OOORange]);
      setOOONewStartDate('');
      setOOONewEndDate('');
      if (result.contributorCache) {
        applyOOOCacheToContributor(oooTargetContributorId, result.contributorCache);
      }
    });
  };
  const handleStartEditOOORange = (range: OOORange) => {
    setOOOError('');
    setOOOEditingRangeId(range.id);
    setOOOEditStartDate(range.oooStartDate);
    setOOOEditEndDate(range.oooEndDate);
  };
  const handleCancelEditOOORange = () => {
    setOOOEditingRangeId(null);
    setOOOEditStartDate('');
    setOOOEditEndDate('');
  };
  const handleSaveEditOOORange = () => {
    if (!requestManagementAccess()) return;
    if (!oooTargetContributorId || !oooEditingRangeId) return;
    setOOOError('');
    if (!oooEditStartDate || !oooEditEndDate) {
      setOOOError('Please select both start and end dates');
      return;
    }
    if (oooEditEndDate < oooEditStartDate) {
      setOOOError('End date must be on or after start date');
      return;
    }
    updateOOORange(oooTargetContributorId, oooEditingRangeId, oooEditStartDate, oooEditEndDate).then(result => {
      if (!result.success || !result.range) {
        setOOOError(result.error || 'Failed to update OOO range');
        return;
      }
      const updatedRange = result.range as OOORange;
      setOOORanges(prev => prev.map(r => r.id === updatedRange.id ? updatedRange : r));
      handleCancelEditOOORange();
      if (result.contributorCache) {
        applyOOOCacheToContributor(oooTargetContributorId, result.contributorCache);
      }
    });
  };
  const handleDeleteOOORange = (range: OOORange) => {
    if (!requestManagementAccess()) return;
    if (!oooTargetContributorId) return;
    setOOOError('');
    deleteOOORange(oooTargetContributorId, range.id).then(result => {
      if (!result.success) {
        setOOOError(result.error || 'Failed to delete OOO range');
        return;
      }
      setOOORanges(prev => prev.filter(r => r.id !== range.id));
      if (result.contributorCache) {
        applyOOOCacheToContributor(oooTargetContributorId, result.contributorCache);
      }
    });
  };
  // Date Availability Handlers
  // Icon click → slide-over panel
  const handleOpenWeekView = (contributorId: string) => {
    setWeekPanelContributorId(contributorId);
  };
  const handleClosePanelView = () => {
    setWeekPanelContributorId(null);
  };
  const handleUpdateDailyAvailability = async (contributorId: string, solutionId: string, date: string, status: AvailabilityStatus | null) => {
    const contributor = contributors.find(c => c.id === contributorId);
    const solution = solutions.find(s => s.id === solutionId);
    const result = await updateDailyAvailability(contributorId, solutionId, date, status, contributor, solution);
    notifyDbResult(result);
  };
  const accessLevelLabel = accessLevel === 'superadmin' ? 'Superadmin' : accessLevel === 'admin' ? 'Admin' : accessLevel === 'editor' ? 'Editor' : accessLevel === 'viewer' ? 'Viewer' : accessLevel === 'unregistered' ? 'Unregistered' : 'Signed out';
  if (isHydrating) {
    return <div className="flex h-screen items-center justify-center bg-slate-50 text-slate-500 font-sans" data-id="element-141" data-mp="19f89q7frwzlz">
        {isAuthReady ? 'Loading resource data...' : 'Initializing Adobe sign-in...'}
      </div>;
  }
  if (!isSignedIn) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-10 text-slate-900 font-sans" data-id="element-142" data-mp="2k0ntlex68l8c">
        <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-id="element-143" data-mp="2f35tme14tgch">
          <div className="flex items-center gap-4" data-id="element-144" data-mp="3cojn6arg1x0w">
            <img src="https://cdn.magicpatterns.com/uploads/6yi4FNzoPfGEun9849jQCu/Screenshot_2026-02-03_at_7.33.48_PM.png" alt="Adobe OnDemand" className="h-14 w-auto" data-id="element-145" data-mp="37qruvfmw08w6" />
            <div data-id="element-146" data-mp="2y18fwl2aoz6l">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-147" data-mp="2y1qevpgcdri7">
                Resource Availability Dashboard
              </h1>
              <p className="mt-1 text-sm text-slate-500" data-id="element-148" data-mp="3vhnkc63ves09">
                Sign in with Adobe ID
              </p>
            </div>
          </div>

          <div className="mt-8 space-y-4" data-id="element-149" data-mp="1py00996zmcmn">
            <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-id="element-150" data-mp="1pxqcztdbujkn">
              Dashboard data is available only after Adobe IMS login.
              <br data-id="element-151" data-mp="1g7evf40q0bva" /> <br data-id="element-152" data-mp="1g7exrao70nyg" />
              <p data-id="element-153" data-mp="1g6vkw8degpra">
              1. Click Sign-In <br data-id="element-154" data-mp="1b9db6frk66i8" />
              2. Login using your company account <br data-id="element-155" data-mp="1b8u63zn0bclm" />
              3. Choose profile : Adobe Inc
              </p>
            </div>

            {authError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-156" data-mp="175p4s0enxgyk">
                {authError}
              </div>}

            <Button onClick={handleAuthButtonClick} variant="primary" size="lg" isLoading={isAuthLoading} data-id="element-157" data-mp="xadq1i3989qt">
              
              <LogIn className="mr-2 h-4 w-4" data-id="element-158" data-mp="2rnvh3f2vboql" />
              Sign in with Adobe ID
            </Button>
          </div>
        </div>
      </div>;
  }
  if (accessLevel === 'unregistered') {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-10 text-slate-900 font-sans" data-id="element-159" data-mp="2v6cbdyftmobg">
        <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-id="element-160" data-mp="2z5tisxet5vj9">
          <div className="flex items-center gap-4" data-id="element-161" data-mp="33yede15jgcnu">
            <img src="https://cdn.magicpatterns.com/uploads/6yi4FNzoPfGEun9849jQCu/Screenshot_2026-02-03_at_7.33.48_PM.png" alt="Adobe OnDemand" className="h-14 w-auto" data-id="element-162" data-mp="38wo7s9jfv98e" />
            <div data-id="element-163" data-mp="2g7icmej3u74g">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-164" data-mp="2g91to7cl4m7a">
                Resource Availability Dashboard
              </h1>
              <p className="mt-1 text-sm text-slate-500" data-id="element-165" data-mp="2l9g39ttu447f">
                Account not provisioned
              </p>
            </div>
          </div>

          <div className="mt-8 space-y-4" data-id="element-166" data-mp="hxhs1iipy6q">
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-id="element-167" data-mp="gw2fkugypj7">
              You are signed in with Adobe ID, but this account is not registered for dashboard access.
              No resource data is shown until an administrator assigns you a role in the system.
            </div>

            {authError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-168" data-mp="3lvx6yrn7ywgt">
                {authError}
              </div>}

            <Button onClick={handleAuthButtonClick} variant="outline" size="lg" isLoading={isAuthLoading} data-id="element-169" data-mp="2t6zn4k3d0rly">
              
              <LogOut className="mr-2 h-4 w-4" data-id="element-170" data-mp="2o5sfqm54puft" />
              Sign out
            </Button>
          </div>
        </div>
      </div>;
  }
  return <div className="flex flex-col h-screen bg-slate-50 text-slate-900 font-sans" data-id="element-171" data-mp="2y5t1eeqh0mn6">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4 shadow-sm z-30" data-id="element-172" data-mp="2afoxs7da8mw6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-173" data-mp="25hx9rofrdkx9">
          <div className="flex items-center gap-3" data-id="element-174" data-mp="kzmo9p6sjise">
            <img src="https://cdn.magicpatterns.com/uploads/6yi4FNzoPfGEun9849jQCu/Screenshot_2026-02-03_at_7.33.48_PM.png" alt="Adobe OnDemand" className="h-12 w-auto" data-id="element-175" data-mp="ky1u40oe21v2" />
            
            <div data-id="element-176" data-mp="azcdscc54pgx">
              <h1 className="text-xl font-bold text-slate-900" data-id="element-177" data-mp="g0093a7ejsgy">
                Resource Availability Dashboard
              </h1>
              <div className="flex items-center gap-2" data-id="element-178" data-mp="13r5r9w0tewuf">
                <p className="text-sm text-slate-500" data-id="element-179" data-mp="18oxl4vl0st19">
                  Track solution coverage and individual capacity
                </p>
                {dbFeedback && <div className={`flex items-center gap-1 text-xs animate-in fade-in duration-200 ${dbFeedback.type === 'success' ? 'text-green-600' : 'text-red-600'}`} data-id="element-180" data-mp="ur8vlag0cxpv">
                    {dbFeedback.type === 'success' ? <Check className="h-3 w-3" data-id="element-181" data-mp="3jhrzik5w44d1" /> : <AlertCircle className="h-3 w-3" data-id="element-182" data-mp="3iqvm56o2mjw" />}
                    <span data-id="element-183" data-mp="3kbolqdc1xfn">{dbFeedback.message}</span>
                  </div>}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap" data-id="element-184" data-mp="3py25zdt5i1h6">
            <div className="relative hidden md:block" data-id="element-185" data-mp="3pylb1txpcvds">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-186" data-mp="3l00atuigabpc" />
              <input type="text" placeholder="Search ICs or solutions..." className="pl-9 pr-4 py-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1000] w-64" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-187" data-mp="mhv1j52ww07c" />
              
            </div>
            <div className="h-8 w-px bg-slate-200 mx-2 hidden md:block" data-id="element-188" data-mp="2x9ec4tuc63s0" />

            {isSignedIn && <div className="hidden lg:flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-600" data-id="element-189" data-mp="2t7jfw3r9igl3">
                <span className={`h-2 w-2 rounded-full ${canManageData ? 'bg-green-500' : 'bg-amber-500'}`} data-id="element-190" data-mp="18rv4aasnr61m" />
                <span className="font-medium text-slate-800" data-id="element-191" data-mp="1hqow7nv1n88u">
                  {user?.displayName || 'Adobe user'}
                </span>
                {user?.email && <span className="text-slate-500" data-id="element-192" data-mp="1miq46xl6ict2">{user.email}</span>}
                <span className={`rounded-full px-2 py-0.5 font-medium ${accessLevel === 'superadmin' || accessLevel === 'admin' ? 'bg-slate-900 text-white' : accessLevel === 'editor' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-193" data-mp="1rfqb1apurjl8">
                  
                  {accessLevelLabel}
                </span>
              </div>}

            <Button onClick={handleAuthButtonClick} variant={isSignedIn ? 'outline' : 'primary'} size="sm" isLoading={isAuthLoading && !isSignedIn} data-id="element-194" data-mp="63ynvj1b4w04">
              
              {isSignedIn ? <LogOut className="h-4 w-4 mr-2" data-id="element-195" data-mp="1ef9xu9mu0vh8" /> : <LogIn className="h-4 w-4 mr-2" data-id="element-196" data-mp="19eun7ptxq22y" />}
              {isSignedIn ? 'Sign Out' : 'Sign in with Adobe ID'}
            </Button>

            {isAdmin && <>
                <Button onClick={handleDownloadCurrentData} variant="outline" size="sm" data-id="element-197" data-mp="1iehng8axh1uf">

                  <Download className="h-4 w-4 mr-2" data-id="element-198" data-mp="1xa72tnllygq6" />
                  Export Data
                </Button>
                <Button onClick={handleDownloadAuditLog} variant="outline" size="sm" isLoading={isExportingLog} data-id="element-199" data-mp="1sauh566h3fp6">

                  <ScrollText className="h-4 w-4 mr-2" data-id="element-200" data-mp="2239z43hrs4a2" />
                  Log
                </Button>
                {/* <Button
                 onClick={() => setIsImportOpen(true)}
                 variant="outline"
                 size="sm">
                                   <Upload className="h-4 w-4 mr-2" />
                  Import
                 </Button> */}
                <Button onClick={() => setIsAddICOpen(true)} variant="outline" size="sm" data-id="element-201" data-mp="2zokzpk7jg8kd">
                
                  <Plus className="h-4 w-4 mr-2" data-id="element-202" data-mp="2ktepsm5bnplr" />
                  Add Contributor
                </Button>
                <Button onClick={() => setIsAddSolutionOpen(true)} variant="primary" size="sm" data-id="element-203" data-mp="2byqlay7kmby0">
                
                  <Plus className="h-4 w-4 mr-2" data-id="element-204" data-mp="2gr05rypx2jsw" />
                  Add Solution
                </Button>
              </>}
          </div>
        </div>

        <div className="flex justify-end" data-id="element-205" data-mp="26z45ch0cyhli">
          <span className="text-xs hover:text-blue-500" data-id="element-206" data-mp="26zx5gz4ta8uo"><a target="_blank" href="https://adobe-my.sharepoint.com/:x:/r/personal/dsagar_adobe_com/_layouts/15/doc2.aspx?sourcedoc=%7B430e4f3f-47f7-428f-9fb3-2c6f5f88ee7d%7D&action=edit" data-id="element-207" data-mp="1szcnu6amuj2r">Feedback</a></span><span className="text-xs ml-1 italic" data-id="element-208" data-mp="2dgp70m300e55">v2.0</span>
        </div>
        {/* Mobile Search */}
        <div className="mt-4 md:hidden relative" data-id="element-209" data-mp="28hcm46voth57">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-210" data-mp="28inny15koeto" />
          <input type="text" placeholder="Search ICs or solutions..." className="w-full pl-9 pr-4 py-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1000]" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-211" data-mp="23gybqi6m5v40" />
          
        </div>

        {(authError || isAuthReady && !isSignedIn || isSignedIn && !canManageData || isSignedIn && canManageData && !isAdmin) && <div className="mt-4 space-y-2" data-id="element-212" data-mp="1fujf56rpflmr">
            {authError && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-213" data-mp="1aunar6f3hs6o">
                {authError}
              </div>}
            {isAuthReady && !isSignedIn && !authError && <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-id="element-214" data-mp="3ieu98n2dcisf">
                Sign in with Adobe ID to unlock
              </div>}
            {isSignedIn && !canManageData && accessLevel === 'viewer' && <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-id="element-215" data-mp="a91y3epqt21">
                Signed in as{' '}
                <span className="font-medium" data-id="element-216" data-mp="2yt7ma608ug7z">
                  {user?.email || user?.displayName || 'Adobe user'}
                </span>.
                This account has viewer access only.
              </div>}
            {isSignedIn && canManageData && !isAdmin && <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700" data-id="element-217" data-mp="3dhaqpayn1juo">
                Signed in as{' '}
                <span className="font-medium" data-id="element-218" data-mp="2g1542q7yervm">
                  {user?.email || user?.displayName || 'Adobe user'}
                </span>.
                
              </div>}
          </div>}
      </header>

      {/* Legend & Active Filters */}
      <div className="bg-white border-b border-slate-200 px-6 py-2" data-id="element-219" data-mp="1zomb3u6bu34x">
        <div className="flex flex-col gap-2" data-id="element-220" data-mp="1lq6565gaggnu">
          <div className="flex items-center justify-between gap-4 flex-wrap" data-id="element-221" data-mp="1lp4jz85k6dse">
            <div className="flex items-center gap-4 text-xs" data-id="element-222" data-mp="1pqpz705kf45r">
              <span className="font-medium text-slate-600" data-id="element-223" data-mp="1c032fdjd7ocb">Status:</span>
              {/* Clickable status filters */}
              <button onClick={() => handleStatusFilterClick('available')} className={`flex items-center gap-2 px-2 py-1 rounded-md transition-all cursor-pointer ${selectedStatusFilter === 'available' ? 'bg-green-100 ring-2 ring-green-500 ring-offset-1' : 'hover:bg-green-50'}`} title="Click to filter by Qualified and Available" data-id="element-224" data-mp="1grbe6g863lsn">
                
                <div className="h-3 w-3 rounded-full bg-green-500" data-id="element-225" data-mp="y0tneh6wb09x"></div>
                <span className={selectedStatusFilter === 'available' ? 'text-green-700 font-medium' : 'text-slate-700'} data-id="element-226" data-mp="y24p8bgs5xye">
                  
                  Qualified and Available
                </span>
              </button>
              <button onClick={() => handleStatusFilterClick('limited')} className={`flex items-center gap-2 px-2 py-1 rounded-md transition-all cursor-pointer ${selectedStatusFilter === 'limited' ? 'bg-yellow-100 ring-2 ring-yellow-500 ring-offset-1' : 'hover:bg-yellow-50'}`} title="Click to filter by Qualified not Available" data-id="element-227" data-mp="jduwcnbya7vq">
                
                <div className="h-3 w-3 rounded-full bg-yellow-400" data-id="element-228" data-mp="124cp2rws93h3"></div>
                <span className={selectedStatusFilter === 'limited' ? 'text-yellow-700 font-medium' : 'text-slate-700'} data-id="element-229" data-mp="126779ygo6hhh">
                  
                  Qualified not Available
                </span>
              </button>
              <button onClick={() => handleStatusFilterClick('unavailable')} className={`flex items-center gap-2 px-2 py-1 rounded-md transition-all cursor-pointer ${selectedStatusFilter === 'unavailable' ? 'bg-red-100 ring-2 ring-red-500 ring-offset-1' : 'hover:bg-red-50'}`} title="Click to filter by Not Qualified" data-id="element-230" data-mp="dqh8w08a6k14">
                
                <div className="h-3 w-3 rounded-full bg-red-500" data-id="element-231" data-mp="rk8fazqptnsm"></div>
                <span className={selectedStatusFilter === 'unavailable' ? 'text-red-700 font-medium' : 'text-slate-700'} data-id="element-232" data-mp="wfe1lzo154h8">
                  
                  Not Qualified
                </span>
              </button>

              <div className="h-4 w-px bg-slate-200 mx-1" data-id="element-233" data-mp="33984yrwnyhgk" />

              <span className="font-medium text-slate-600" data-id="element-234" data-mp="37a1prcet7g58">Level:</span>
              {/* Clickable experience level filters */}
              <button onClick={() => handleExperienceFilterClick('L1')} className={`relative flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer group ${selectedExperienceFilter === 'L1' ? 'bg-slate-200 ring-2 ring-slate-400 ring-offset-1' : 'hover:bg-slate-50'}`} title="Click to filter by L1 - Foundational" data-id="element-235" data-mp="2zqsgrka7tjgp">
                
                <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-200" data-id="element-236" data-mp="3ink6ock9iwtq">
                  L1
                </span>
                <span className={selectedExperienceFilter === 'L1' ? 'text-slate-800 font-medium' : 'text-slate-700'} data-id="element-237" data-mp="39mm3txlk8vcq">
                  
                  Foundational
                </span>
                <span className="z-30 pointer-events-none absolute left-1/2 top-full z-20 mt-2 pb-4 hidden w-56 -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block" data-id="element-238" data-mp="1t95n06o3q053">
                  FOUNDATIONAL

                  <p className="text-m font-boltext-m font-bold mt-2" data-id="element-239" data-mp="385ot6e75ffnb">Enablement Requirements:</p>
                  <p data-id="element-240" data-mp="3c6qssw4z9tpt">Intro-level enablement completed with understanding of the basics and value of the solution.</p>

                  <p className="text-m font-boltext-m font-bold mt-2" data-id="element-241" data-mp="3qysiluv807h">Tooling Expectations:</p>
                  <p data-id="element-242" data-mp="8qjqasyau2im">Learners can do a standard demo in a clickable and shared environment.</p>

                  <p className="text-m font-boltext-m font-bold mt-2" data-id="element-243" data-mp="3qwfyn4o0mlwf">Field Support Implications:</p>
                  <p data-id="element-244" data-mp="3vuhoq9juzlhe">Learners are apprenticing in the field and can support 1:Many demos.</p>

                  <p className="text-m font-boltext-m font-bold mt-2" data-id="element-245" data-mp="2fg81xjv9m4so">Outcome:</p>
                  <p data-id="element-246" data-mp="2kfuh3yunlv44">Learners should be able to deliver a value pitch and 101 level demo.</p>
                </span>
              </button>
              <button onClick={() => handleExperienceFilterClick('L2')} className={`relative flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer group ${selectedExperienceFilter === 'L2' ? 'bg-blue-100 ring-2 ring-blue-400 ring-offset-1' : 'hover:bg-blue-50'}`} title="Click to filter by L2 - Intermediate" data-id="element-247" data-mp="2bxsisqnobwzo">
                
                <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-blue-50 text-blue-600 border-blue-200" data-id="element-248" data-mp="1t9dptxtsq8w8">
                  L2
                </span>
                <span className={selectedExperienceFilter === 'L2' ? 'text-blue-700 font-medium' : 'text-slate-700'} data-id="element-249" data-mp="22xvoo9iim5x8">
                  
                  Intermediate
                </span>
                <span className="z-30 pointer-events-none absolute left-1/2 top-full z-20 mt-2 pb-4 hidden w-56 -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block" data-id="element-250" data-mp="1afi1d3ut22ce">
                  INTERMEDIATE

                  <p className="font-bold mt-2" data-id="element-251" data-mp="3ahzngh61h65h">Enablement Requirements:</p>
                  <p data-id="element-252" data-mp="3fc3xj9alpuas">Deeper dives into features, customer use Requests, and common integrations.</p>

                  <p className="font-bold mt-2" data-id="element-253" data-mp="3jbw6hn3wq2go">Tooling Expectations:</p>
                  <p data-id="element-254" data-mp="2mmwym978j2p0">Learners can do an ad hoc demo in a live environment.</p>

                  <p className="font-bold mt-2" data-id="element-255" data-mp="2rkyekl9d6lw9">Field Support Implications:</p>
                  <p data-id="element-256" data-mp="2rn2slw0z91a9">Learners are supporting early-stage or low complexity single solution customer meetings.</p>

                  <p className="font-bold mt-2" data-id="element-257" data-mp="30lo8fjf2e27c">Outcome:</p>
                  <p data-id="element-258" data-mp="30n7pvdcgijap">Learners have completed numerous customer demos that show practical application. They should be doing ad hoc demos where they’re able to go off script.</p>
                </span>
              </button>
              <button onClick={() => handleExperienceFilterClick('L3')} className={`relative flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer group ${selectedExperienceFilter === 'L3' ? 'bg-indigo-100 ring-2 ring-indigo-400 ring-offset-1' : 'hover:bg-indigo-50'}`} title="Click to filter by L3 - Advanced" data-id="element-259" data-mp="fxib10br6xi0">
                
                <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-indigo-50 text-indigo-600 border-indigo-200" data-id="element-260" data-mp="c6ehppzb3arl">
                  L3
                </span>
                <span className={selectedExperienceFilter === 'L3' ? 'text-indigo-700 font-medium' : 'text-slate-700'} data-id="element-261" data-mp="28gn8kqushqj">
                  
                  Advanced
                </span>
                <span className="z-30 pointer-events-none absolute left-1/2 top-full z-20 mt-2 pb-4 hidden w-56 -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block" data-id="element-262" data-mp="1rnof7w1mgbzf">
                  ADVANCED

                  <p className="font-bold mt-2" data-id="element-263" data-mp="2axzd1p4in5zt">Enablement Requirements:</p>
                  <p data-id="element-264" data-mp="1x6cipuu4w7jo">Covers integrations with other solutions, data migration architectures, and expert-level guidance.</p>

                  <p className="font-bold mt-2" data-id="element-265" data-mp="224mcq244h23p">Tooling Expectations:</p>
                  <p data-id="element-266" data-mp="1n8525d3rbri7">Learners can demo product integrations and add-ons in a live environment.</p>

                  <p className="font-bold mt-2" data-id="element-267" data-mp="1s787yzrka1r8">Field Support Implications:</p>
                  <p data-id="element-268" data-mp="1s557tmwp2s8s">Learners are supporting all single solution customer meetings.</p>

                  <p className="font-bold mt-2" data-id="element-269" data-mp="1e9b7vtdwfd7t">Outcome:</p>
                  <p data-id="element-270" data-mp="1j5ryqvchg5ol">Learners can easily lead ad hoc demos and go off script live with a customer. Learners have a comprehensive understanding of the tool and multiple closed deals to show practical application.</p>
                </span>
              </button>
              <button onClick={() => handleExperienceFilterClick('L4')} className={`relative flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer group ${selectedExperienceFilter === 'L4' ? 'bg-violet-100 ring-2 ring-violet-400 ring-offset-1' : 'hover:bg-violet-50'}`} title="Click to filter by L4 - Champion" data-id="element-271" data-mp="3di8aucryme1d">
                
                <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-violet-50 text-violet-700 border-violet-200" data-id="element-272" data-mp="3roa0ujql9vbo">
                  L4
                </span>
                <span className={selectedExperienceFilter === 'L4' ? 'text-violet-700 font-medium' : 'text-slate-700'} data-id="element-273" data-mp="5aa4s4v6f6jr">
                  
                  Champion
                </span>
                <span className="z-30 pointer-events-none absolute left-1/2 top-full z-20 mt-2 pb-4 hidden w-56 -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block" data-id="element-274" data-mp="2l2fs4ga68j2k">
                  CHAMPION

                  <p className="font-bold mt-2" data-id="element-275" data-mp="1jfszkjjwtpgh">Field Support Implications:</p>
                  <p data-id="element-276" data-mp="1y3nt225qrxum">Learners can support architecture and multi-solution conversations. The most complex meetings.</p>

                  <p className="font-bold mt-2" data-id="element-277" data-mp="1t4knmglunpm4">Outcome:</p>
                  <p data-id="element-278" data-mp="1t76x4lsawf6z">Learners are the SME on the team for an individual product and a thought leader as the product evolves.</p>
                </span>
              </button>

              <div className="h-4 w-px bg-slate-200 mx-1" data-id="element-279" data-mp="10hzxfjh41jk5" />

              <span className="font-medium text-slate-600" data-id="element-280" data-mp="1eg6lk7w9c56m">Region:</span>
              {REGIONS.map(region => {
              const isActive = selectedRegionFilter === region;
              const colorMap: Record<Region, {
                active: string;
                hover: string;
                text: string;
                dot: string;
              }> = {
                USA: {
                  active: 'bg-blue-100 ring-2 ring-blue-400 ring-offset-1',
                  hover: 'hover:bg-blue-50',
                  text: 'text-blue-700 font-medium',
                  dot: 'bg-blue-500'
                },
                LATAM: {
                  active: 'bg-amber-100 ring-2 ring-amber-400 ring-offset-1',
                  hover: 'hover:bg-amber-50',
                  text: 'text-amber-700 font-medium',
                  dot: 'bg-amber-500'
                },
                India: {
                  active: 'bg-emerald-100 ring-2 ring-emerald-400 ring-offset-1',
                  hover: 'hover:bg-emerald-50',
                  text: 'text-emerald-700 font-medium',
                  dot: 'bg-emerald-500'
                },
                EMEA: {
                  active: 'bg-purple-100 ring-2 ring-purple-400 ring-offset-1',
                  hover: 'hover:bg-purple-50',
                  text: 'text-purple-700 font-medium',
                  dot: 'bg-purple-500'
                }
              };
              const colors = colorMap[region];
              return <button key={region} onClick={() => handleRegionFilterClick(region)} className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer ${isActive ? colors.active : colors.hover}`} title={`Click to filter by ${region}`} data-id="element-281" data-mp="3vpkfoeemhjxm">
                    
                    <div className={`h-2.5 w-2.5 rounded-sm ${colors.dot}`} data-id="element-282" data-mp="3h0nx3nexwoir" />
                    <span className={isActive ? colors.text : 'text-slate-700'} data-id="element-283" data-mp="3d040ckya3mx5">
                      {region}
                    </span>
                  </button>;
            })}
            </div>
          </div>

          {/* Active Filters */}
          {hasActiveFilters && <div className="flex items-center gap-2" data-id="element-284" data-mp="1ijdpoo9ugllf">
              <span className="text-xs font-medium text-slate-600" data-id="element-285" data-mp="1ikym6jfpyelx">
                Filtered:
              </span>
              {selectedStatusFilter && <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${STATUS_COLORS[selectedStatusFilter].bg} ${STATUS_COLORS[selectedStatusFilter].text}`} data-id="element-286" data-mp="1shtj4xwcg6y3">
              
                  {STATUS_LABELS[selectedStatusFilter]}
                  <button onClick={() => setSelectedStatusFilter(null)} className={`hover:${STATUS_COLORS[selectedStatusFilter].bg} rounded-full p-0.5`} data-id="element-287" data-mp="14qxw0e2u1zx6">
                
                    <X className="h-3 w-3" data-id="element-288" data-mp="1dj9ar2ol3yxb" />
                  </button>
                </span>}
              {selectedExperienceFilter && <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${selectedExperienceFilter === 'L1' ? 'bg-slate-100 text-slate-700' : selectedExperienceFilter === 'L2' ? 'bg-blue-100 text-blue-700' : selectedExperienceFilter === 'L3' ? 'bg-indigo-100 text-indigo-700' : 'bg-violet-100 text-violet-700'}`} data-id="element-289" data-mp="h13jjxmfeddg">
              
                  {selectedExperienceFilter} –{' '}
                  {EXPERIENCE_LABELS[selectedExperienceFilter]}
                  <button onClick={() => setSelectedExperienceFilter(null)} className="hover:bg-white/50 rounded-full p-0.5" data-id="element-290" data-mp="6wn4v7kon2b8">
                
                    <X className="h-3 w-3" data-id="element-291" data-mp="fq8gwj5gq5gy" />
                  </button>
                </span>}
              {selectedRegionFilter && <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${selectedRegionFilter === 'USA' ? 'bg-blue-100 text-blue-700' : selectedRegionFilter === 'LATAM' ? 'bg-amber-100 text-amber-700' : selectedRegionFilter === 'India' ? 'bg-emerald-100 text-emerald-700' : 'bg-purple-100 text-purple-700'}`} data-id="element-292" data-mp="umqx6isf7fmx">
              
                  {selectedRegionFilter}
                  <button onClick={() => setSelectedRegionFilter(null)} className="hover:bg-white/50 rounded-full p-0.5" data-id="element-293" data-mp="16y0b6hrxyvew">
                
                    <X className="h-3 w-3" data-id="element-294" data-mp="x8grxb0bgzji" />
                  </button>
                </span>}
              {(selectedStatusFilter || selectedExperienceFilter || selectedRegionFilter) && [selectedStatusFilter, selectedExperienceFilter, selectedRegionFilter].filter(Boolean).length > 1 && <span className="text-[10px] text-slate-400 italic" data-id="element-295" data-mp="e9cmec7zhnwl">
                    (combined filters)
                  </span>}
              {selectedContributor && <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs" data-id="element-296" data-mp="28muco6zrwyvb">
                  {selectedContributor.name}
                  <button onClick={() => setSelectedContributorId(null)} className="hover:bg-blue-200 rounded-full p-0.5" data-id="element-297" data-mp="2dlf53k8sae97">
                
                    <X className="h-3 w-3" data-id="element-298" data-mp="274nahp5qapek" />
                  </button>
                </span>}
              {selectedSolution && <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs" data-id="element-299" data-mp="228og9kxpyd68">
                  {selectedSolution.name}
                  <button onClick={() => setSelectedSolutionId(null)} className="hover:bg-purple-200 rounded-full p-0.5" data-id="element-300" data-mp="2uu7vsepctyr0">
                
                    <X className="h-3 w-3" data-id="element-301" data-mp="2l4y3axqlme2d" />
                  </button>
                </span>}
              <button onClick={handleClearFilters} className="text-xs text-slate-500 hover:text-slate-700 underline" data-id="element-302" data-mp="2g6wlsh8tmmt0">
              
                Clear all
              </button>
            </div>}
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-hidden p-4 md:p-6" data-id="element-303" data-mp="3h3y67q6l2lcf">
        {weekViewContributorId ? (() => {
        const weekContributor = contributors.find(c => c.id === weekViewContributorId);
        if (!weekContributor) return null;
        return <div className="h-full rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col" data-id="element-304" data-mp="3q31pov9nx2rg">
              <ContributorWeekView contributor={weekContributor} solutions={solutions} baseAvailability={availability} canEdit={canManageData} onClose={handleCloseWeekView} fetchCurrentWeek={fetchCurrentWeekSchedule} fetchNextWeek={fetchNextWeekSchedule} onUpdateDailyAvailability={(sId, date, status) => handleUpdateDailyAvailability(weekContributor.id, sId, date, status)} showTabs={false} data-id="element-305" data-mp="3x4se4z80psc" />
            </div>;
      })() : <AvailabilityGrid contributors={statusFilteredContributors} solutions={statusFilteredSolutions} availability={availability} onToggleAvailability={handleToggleAvailability} onDeleteContributor={handleDeleteContributor} onDeleteSolution={handleDeleteSolution} onUpdateContributorRegion={handleUpdateContributorRegion} onContributorClick={handleContributorClick} onSolutionClick={handleSolutionClick} selectedContributorId={selectedContributorId} selectedSolutionId={selectedSolutionId} selectedStatusFilter={selectedStatusFilter} selectedExperienceFilter={selectedExperienceFilter} onReorderContributors={handleReorderContributors} onReorderSolutions={handleReorderSolutions} isAdminAuthenticated={canManageData} canAdministerEntities={isAdmin} canDeleteEntities={isAdmin} allContributors={contributors} allSolutions={solutions} onEditContributor={handleEditContributor} onEditSolution={handleEditSolution} onToggleOutOfOffice={handleOpenOOOModal} experienceLevels={experienceLevels} onCycleExperienceLevel={handleCycleExperienceLevel} onOpenWeekView={hasDashboardDataAccess ? handleOpenWeekView : undefined} data-id="element-306" data-mp="1cxfj8xpe3i8s" />}
      </main>

      {/* Weekly schedule slide-over panel (icon click) */}
      {weekPanelContributorId && (() => {
      const panelContributor = contributors.find(c => c.id === weekPanelContributorId);
      if (!panelContributor) return null;
      return <div className="fixed inset-y-0 right-0 z-40 flex" data-id="element-307" data-mp="2vvu68pzkezw8">
            <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" onClick={handleClosePanelView} data-id="element-308" data-mp="2vy6ol498jc9t" />
            <div className="relative ml-auto w-full max-w-3xl bg-white shadow-xl flex flex-col border-l border-slate-200" data-id="element-309" data-mp="1khgn1yfrb8mj">
              <ContributorWeekView contributor={panelContributor} solutions={solutions} baseAvailability={availability} canEdit={canManageData} onClose={handleClosePanelView} fetchCurrentWeek={fetchCurrentWeekSchedule} fetchNextWeek={fetchNextWeekSchedule} onUpdateDailyAvailability={(sId, date, status) => handleUpdateDailyAvailability(panelContributor.id, sId, date, status)} data-id="element-310" data-mp="1sxiz1ycn1kfw" />
            </div>
          </div>;
    })()}

      {/* Import Modal */}
      <Modal isOpen={isImportOpen} onClose={() => {
      setIsImportOpen(false);
      setImportError('');
    }} title="Import Availability Data" footer={<div className="flex justify-between items-center w-full" data-id="element-312" data-mp="rez07ccsiqyj">
            <div className="flex gap-2" data-id="element-313" data-mp="wev3tahksgdk">
              <Button variant="outline" onClick={handleDownloadTemplate} size="sm" data-id="element-314" data-mp="wdtlcky8crlt">
              
                <FileSpreadsheet className="h-4 w-4 mr-2" data-id="element-315" data-mp="35pvuxidk39ko" />
                Blank Template
              </Button>
              <Button variant="outline" onClick={handleDownloadCurrentData} size="sm" data-id="element-316" data-mp="30og7jjnwoqz4">
              
                <Download className="h-4 w-4 mr-2" data-id="element-317" data-mp="2qrjxvam9tysz" />
                Current Data
              </Button>
            </div>
            <Button variant="ghost" onClick={() => setIsImportOpen(false)} data-id="element-318" data-mp="2m0bnab97g7e8">
              Close
            </Button>
          </div>} data-id="element-311" data-mp="1k3nsqmzs5nyo">
        
        <div className="space-y-4" data-id="element-319" data-mp="2hzhzrizoculv">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-320" data-mp="2d0f5mtl8g8so">
            <h4 className="font-medium text-blue-900 text-sm mb-2" data-id="element-321" data-mp="2cyc5vhua31ar">
              CSV Format Instructions
            </h4>
            <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside" data-id="element-322" data-mp="23298epadfqu5">
              <li data-id="element-323" data-mp="1tedbhktvbk95">
                First column: <strong data-id="element-324" data-mp="1te3tokiz8jej">Contributor</strong> (name)
              </li>
              <li data-id="element-325" data-mp="1ydgihates0jr">
                Second column: <strong data-id="element-326" data-mp="1yehsrd2loj0f">Region</strong> (USA, LATAM, India, or
                EMEA)
              </li>
              <li data-id="element-327" data-mp="1keqjfpn13qsr">Remaining columns: Solution names</li>
              <li data-id="element-328" data-mp="1ofahqwjc90gh">
                Cell format:{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-329" data-mp="1og22fcbeddrz">status</code> or{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-330" data-mp="2c4lcnxe0c0mp">status:level</code>
              </li>
              <li data-id="element-331" data-mp="2h47oq3i3natx">
                Status values:{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-332" data-mp="2h5sihqwlapqq">available</code>,{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-333" data-mp="23cazq8bq9h1r">limited</code>,{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-334" data-mp="27ctrk2dmen3y">unavailable</code>
              </li>
              <li data-id="element-335" data-mp="2v2xjvdlcz0fn">
                Level values:{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-336" data-mp="2zufm04z4b727">L1</code>{' '}
                (Foundational),{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-337" data-mp="of70jm0yf57b">L2</code>{' '}
                (Intermediate),{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-338" data-mp="jgx0ax0dheeu">L3</code> (Advanced),{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-339" data-mp="y5090e5sv47c">L4</code> (Champion)
              </li>
              <li data-id="element-340" data-mp="t6gnufz15wg1">
                Examples:{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-341" data-mp="t6oz63fi8tp4">available:L3</code>,{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-342" data-mp="5hnpydyuleis">limited:L1</code>,{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-343" data-mp="ib2br05k3f5">unavailable</code>
              </li>
              <li data-id="element-344" data-mp="ffblv786ws2j">
                Leave cells empty to default to "unavailable" with no level
              </li>
            </ul>
          </div>

          {importError && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-id="element-345" data-mp="1qtxfzl4k9eks">
              {importError}
            </div>}

          <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-slate-400 transition-colors" data-id="element-346" data-mp="1lu2pvjsm0t0s">
            <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileUpload} className="hidden" id="csv-upload" data-id="element-347" data-mp="3rlhiqapv7xfe" />
            
            <label htmlFor="csv-upload" className="cursor-pointer" data-id="element-348" data-mp="9e1hn8xb3aix">
              <Upload className="h-12 w-12 mx-auto text-slate-400 mb-3" data-id="element-349" data-mp="9b5jbjhjqjty" />
              <p className="text-sm font-medium text-slate-700 mb-1" data-id="element-350" data-mp="ec39ojcpmk6j">
                Click to upload CSV file
              </p>
              <p className="text-xs text-slate-500" data-id="element-351" data-mp="j9cybws9yrtb">or drag and drop</p>
            </label>
          </div>

          <p className="text-xs text-slate-500" data-id="element-352" data-mp="s3zyadf66by6">
            <strong data-id="element-353" data-mp="s2q0lpc8b5s2">Note:</strong> Importing will replace all current data.
            Download your current data first if you want to keep it.
          </p>
        </div>
      </Modal>

      {/* Add Contributor Modal */}
      <Modal isOpen={isAddICOpen} onClose={() => {
      setIsAddICOpen(false);
      setNewICNameError('');
      setNewICEmailError('');
    }} title="Add New Contributor" footer={<div className="flex justify-end gap-3" data-id="element-355" data-mp="175pzobcx3kkj">
            <Button variant="ghost" onClick={() => setIsAddICOpen(false)} data-id="element-356" data-mp="127ers666nk4y">
              Cancel
            </Button>
            <Button onClick={handleAddContributor} data-id="element-357" data-mp="y7eeioutlezh">Add Contributor</Button>
          </div>} data-id="element-354" data-mp="122gtjml55cq1">
        
        <div className="space-y-4" data-id="element-358" data-mp="1uvald20ggvxi">
          <p className="text-sm text-slate-500" data-id="element-359" data-mp="1uygagh933xt8">
            Enter the name and region of the individual contributor you would
            like to add to the dashboard.
          </p>
          <Input label="Full Name" placeholder="e.g. Jane Doe" value={newICName} onChange={e => {
          setNewICName(e.target.value);
          setNewICNameError('');
        }} error={newICNameError} autoFocus onKeyDown={e => e.key === 'Enter' && handleAddContributor()} data-id="element-360" data-mp="1l4zi7holiqro" />

          <Input label="Email" placeholder="e.g. jane.doe@adobe.com" type="email" value={newICEmail} onChange={e => {
          setNewICEmail(e.target.value);
          setNewICEmailError('');
        }} error={newICEmailError} onKeyDown={e => e.key === 'Enter' && handleAddContributor()} data-id="element-361" data-mp="6z4hdar6bckn" />

          <div data-id="element-362" data-mp="26jp4dnx17j8">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-363" data-mp="261hkkvsz6w0">
              Region
            </label>
            <select value={newICRegion} onChange={e => setNewICRegion(e.target.value as Region)} className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1000] focus:border-transparent" data-id="element-364" data-mp="3pcu72buvt8x6">
              
              {REGIONS.map(region => <option key={region} value={region} data-id="element-365" data-mp="18ozr6womzof3">
                  {region}
                </option>)}
            </select>
          </div>
        </div>
      </Modal>

      {/* Add Solution Modal */}
      <Modal isOpen={isAddSolutionOpen} onClose={() => {
      setIsAddSolutionOpen(false);
      setNewSolutionNameError('');
    }} title="Add New Solution" footer={<div className="flex justify-end gap-3" data-id="element-367" data-mp="37wg8sb15b0nm">
            <Button variant="ghost" onClick={() => setIsAddSolutionOpen(false)} data-id="element-368" data-mp="333wtn9mt7xgp">
              Cancel
            </Button>
            <Button onClick={handleAddSolution} data-id="element-369" data-mp="3guifn77k8nit">Add Solution</Button>
          </div>} data-id="element-366" data-mp="2y391ulqrbclf">
        
        <div className="space-y-4" data-id="element-370" data-mp="3ctgf8n1wq5f9">
          <p className="text-sm text-slate-500" data-id="element-371" data-mp="3cwkovzy55tdb">
            Enter the name of the new Adobe solution or product area.
          </p>
          <Input label="Solution Name" placeholder="e.g. Adobe Firefly Video" value={newSolutionName} onChange={e => {
          setNewSolutionName(e.target.value);
          setNewSolutionNameError('');
        }} error={newSolutionNameError} autoFocus onKeyDown={e => e.key === 'Enter' && handleAddSolution()} data-id="element-372" data-mp="3luemde7n6t22" />
          
        </div>
      </Modal>

      {/* Edit Contributor Modal */}
      <Modal isOpen={isEditContributorOpen} onClose={() => {
      setIsEditContributorOpen(false);
      setEditingContributor(null);
      setEditContributorName('');
    }} title="Edit Contributor" footer={<div className="flex justify-end gap-3" data-id="element-374" data-mp="mpnb52vhezdp">
            <Button variant="ghost" onClick={() => {
        setIsEditContributorOpen(false);
        setEditingContributor(null);
        setEditContributorName('');
      }} data-id="element-375" data-mp="mqy9gn65rexf">

              Cancel
            </Button>
            <Button onClick={handleSaveContributor} data-id="element-376" data-mp="11mw6s8rtipb8">Save Changes</Button>
          </div>} data-id="element-373" data-mp="1akzrx3yw5k2i">

        <div className="space-y-4" data-id="element-377" data-mp="281ex4r8ajgx8">
          <p className="text-sm text-slate-500" data-id="element-378" data-mp="284j6s44iz4va">
            Update the contributor's name and region.
          </p>
          <Input label="Full Name" placeholder="e.g. Jane Doe" value={editContributorName} onChange={e => setEditContributorName(e.target.value)} autoFocus onKeyDown={e => e.key === 'Enter' && handleSaveContributor()} data-id="element-379" data-mp="1dodeyjz5jod" />

          <div data-id="element-380" data-mp="3iupe6rjzlvgi">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-381" data-mp="3iuxpif0gospl">
              Region
            </label>
            <select value={editContributorRegion} onChange={e => setEditContributorRegion(e.target.value as Region)} className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1000] focus:border-transparent" data-id="element-382" data-mp="3dwe7gpozo12i">

              {REGIONS.map(region => <option key={region} value={region} data-id="element-383" data-mp="131ytuy6v0imj">
                  {region}
                </option>)}
            </select>
          </div>

          <div className="flex items-center justify-between" data-id="element-384" data-mp="2zg0zhlxc8x81">
            <div data-id="element-385" data-mp="2zhbyz9jr3itc">
              <label className="block text-sm font-medium text-slate-700" data-id="element-386" data-mp="2zhvjmy0sclb2">
                Display
              </label>
              <p className="text-xs text-slate-500" data-id="element-387" data-mp="2qnh4cvg99ary">
                Hide this contributor from viewers and editors. Admins will still see them.
              </p>
            </div>
            <button type="button" role="switch" aria-checked={editContributorDisplay} onClick={() => setEditContributorDisplay(prev => !prev)} className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${editContributorDisplay ? 'bg-[#EB1000]' : 'bg-slate-300'}`} data-id="element-388" data-mp="2lmt5jnlsbpn6">

              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editContributorDisplay ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-389" data-mp="1xz1tml45pemf" />

            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Solution Modal */}
      <Modal isOpen={isEditSolutionOpen} onClose={() => {
      setIsEditSolutionOpen(false);
      setEditingSolution(null);
      setEditSolutionName('');
    }} title="Edit Solution" footer={<div className="flex justify-end gap-3" data-id="element-391" data-mp="1xm8zrd4gsmcp">
            <Button variant="ghost" onClick={() => {
        setIsEditSolutionOpen(false);
        setEditingSolution(null);
        setEditSolutionName('');
      }} data-id="element-392" data-mp="21n1aybwwd1bm">
            
              Cancel
            </Button>
            <Button onClick={handleSaveSolution} data-id="element-393" data-mp="2biux8n0f496l">Save Changes</Button>
          </div>} data-id="element-390" data-mp="1nulexv2ey3k7">
        
        <div className="space-y-4" data-id="element-394" data-mp="h6o78i2v7nu8">
          <p className="text-sm text-slate-500" data-id="element-395" data-mp="h7g0vncwjbhv">Update the solution name.</p>
          <Input label="Solution Name" placeholder="e.g. Adobe Firefly Video" value={editSolutionName} onChange={e => setEditSolutionName(e.target.value)} autoFocus onKeyDown={e => e.key === 'Enter' && handleSaveSolution()} data-id="element-396" data-mp="m5hjy8abvatc" />
          
        </div>
      </Modal>

      {/* OOO Range History Modal */}
      <Modal isOpen={isOOOModalOpen} onClose={() => setIsOOOModalOpen(false)} title="Manage Out of Office" footer={<div className="flex justify-end w-full" data-id="element-398" data-mp="x2w2aabe5jqa">
            <Button variant="ghost" onClick={() => setIsOOOModalOpen(false)} data-id="element-399" data-mp="s4uifn655gdr">
              Close
            </Button>
          </div>} data-id="element-397" data-mp="o0wk3eon49lr">

        <div className="space-y-4" data-id="element-400" data-mp="16spcb6vvxqsf">
          {(() => {
          const contributor = contributors.find(c => c.id === oooTargetContributorId);
          return contributor ? <div className="bg-slate-50 p-3 rounded-md border border-slate-200" data-id="element-401" data-mp="1fv89pbgwjb7a">
                <p className="text-sm text-slate-600" data-id="element-402" data-mp="1btv6za93vo4l">
                  Out of Office ranges for{' '}
                  <span className="font-semibold text-slate-900" data-id="element-403" data-mp="1ui6yszsadtiu">
                    {contributor.name}
                  </span>
                </p>
              </div> : null;
        })()}

          <p className="text-sm text-slate-500" data-id="element-404" data-mp="1lfo0mszg452x">
            Add any number of out-of-office ranges. Ranges longer than 25 days
            are flagged as Long Leave. Past ranges are locked and can no longer
            be edited or deleted.
          </p>

          {oooRangesLoading && <p className="text-sm text-slate-400" data-id="element-405" data-mp="1ghmluk91yrx9">Loading…</p>}

          {!oooRangesLoading && <div className="space-y-2 max-h-64 overflow-y-auto" data-id="element-406" data-mp="12qzi2dofqbub">
              {oooRanges.length === 0 && <p className="text-sm text-slate-400" data-id="element-407" data-mp="16scputb38p3v">No OOO ranges yet.</p>}
              {[...oooRanges].sort((a, b) => a.oooStartDate.localeCompare(b.oooStartDate)).map(range => {
            const today = new Date().toISOString().split('T')[0];
            const isPast = range.oooEndDate < today;
            const isActive = range.oooStartDate <= today && range.oooEndDate >= today;
            const isEditing = oooEditingRangeId === range.id;
            return <div key={range.id} className={`p-3 rounded-md border text-sm ${isPast ? 'bg-slate-50 border-slate-200 text-slate-400' : isActive ? 'bg-orange-50 border-orange-200 text-orange-700' : 'bg-blue-50 border-blue-200 text-blue-700'}`} data-id="element-408" data-mp="1r9moro67llpx">

                    {isEditing ? <div className="space-y-2" data-id="element-409" data-mp="18ctx1wcowsxp">
                        <div className="grid grid-cols-2 gap-3" data-id="element-410" data-mp="18ddd17is5793">
                          <Input type="date" label="Start Date" value={oooEditStartDate} onChange={e => setOOOEditStartDate(e.target.value)} data-id="element-411" data-mp="13es84usl1zeb" />

                          <Input type="date" label="End Date" value={oooEditEndDate} onChange={e => setOOOEditEndDate(e.target.value)} min={oooEditStartDate || undefined} data-id="element-412" data-mp="2xpys03nw1tsd" />

                        </div>
                        <div className="flex gap-2 justify-end" data-id="element-413" data-mp="3k0ym2zb83m0w">
                          <Button variant="ghost" size="sm" onClick={handleCancelEditOOORange} data-id="element-414" data-mp="3k29s75s555v6">
                            Cancel
                          </Button>
                          <Button size="sm" onClick={handleSaveEditOOORange} data-id="element-415" data-mp="2rckscvr63bv5">
                            Save
                          </Button>
                        </div>
                      </div> : <div className="flex items-center justify-between gap-3" data-id="element-416" data-mp="30e10sxyf93wq">
                        <div data-id="element-417" data-mp="bjls11mmedlr">
                          <div className="font-medium" data-id="element-418" data-mp="bkdosfryehdm">
                            {new Date(range.oooStartDate).toLocaleDateString()} – {new Date(range.oooEndDate).toLocaleDateString()}
                          </div>
                          <div className="flex gap-2 mt-1 text-[11px]" data-id="element-419" data-mp="2copi5duiz2me">
                            {range.isLongLeave && <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-full border border-purple-200 font-semibold" data-id="element-420" data-mp="2lo2wokxiahdz">
                                Long Leave
                              </span>}
                            {isActive && <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded-full border border-orange-200" data-id="element-421" data-mp="1oxk5t8nsmsgu">
                                Active
                              </span>}
                            {isPast && <span className="px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded-full border border-slate-200" data-id="element-422" data-mp="1xvvsizljh640">
                                Past
                              </span>}
                            {!isPast && !isActive && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full border border-blue-200" data-id="element-423" data-mp="3gv3irivgsv4u">
                                Upcoming
                              </span>}
                          </div>
                        </div>
                        {!isPast && <div className="flex gap-2" data-id="element-424" data-mp="3csyt0v850inh">
                            <button onClick={() => handleStartEditOOORange(range)} className="p-1.5 text-slate-400 hover:text-slate-700 transition-colors" title="Edit range" data-id="element-425" data-mp="4gaz9oyypwyz">

                              <Pencil className="h-3.5 w-3.5" data-id="element-426" data-mp="3qou62kfuuziy" />
                            </button>
                            <button onClick={() => handleDeleteOOORange(range)} className="p-1.5 text-slate-400 hover:text-red-600 transition-colors" title="Delete range" data-id="element-427" data-mp="2f7ati1gdxk7q">

                              <Trash2 className="h-3.5 w-3.5" data-id="element-428" data-mp="3buqhtjbanalh" />
                            </button>
                          </div>}
                      </div>}
                  </div>;
          })}
            </div>}

          <div className="pt-3 border-t border-slate-200 space-y-3" data-id="element-429" data-mp="2wx7tgi2j2zek">
            <p className="text-sm font-medium text-slate-700" data-id="element-430" data-mp="2wz2fjzpjcxk8">Add a new range</p>
            <div className="grid grid-cols-2 gap-4" data-id="element-431" data-mp="3ujoofgnsh0x5">
              <Input type="date" label="Start Date" value={oooNewStartDate} onChange={e => setOOONewStartDate(e.target.value)} data-id="element-432" data-mp="3pl3o7h8jeh8p" />

              <Input type="date" label="End Date" value={oooNewEndDate} onChange={e => setOOONewEndDate(e.target.value)} min={oooNewStartDate || undefined} data-id="element-433" data-mp="3fw3otwq4hjtz" />

            </div>

            {oooNewStartDate && oooNewEndDate && isLongLeaveRange(oooNewStartDate, oooNewEndDate) && <p className="text-sm font-semibold text-purple-700" data-id="element-434" data-mp="kpkcjc4w79ps">
                ⏳ This will be flagged as Long Leave ({calendarDaysInclusive(oooNewStartDate, oooNewEndDate)} days)
              </p>}

            {oooError && <p className="text-sm text-red-600" data-id="element-435" data-mp="ymzc39ov0xvh">{oooError}</p>}

            <div className="flex justify-end" data-id="element-436" data-mp="yl5zzevh9lg3">
              <Button size="sm" onClick={handleAddOOORange} isLoading={oooAddingRange} data-id="element-437" data-mp="13n55auas2sfo">
                Add range
              </Button>
            </div>
          </div>
        </div>
      </Modal>

    </div>;
}